/**
 * 車款管理 /vehicle-models（20260611 反饋批次）
 * 表格＋新增/編輯 modal＋啟用/停用切換；照 PricingRules.tsx 版型。
 */
import { useState } from "react";
import { Plus, Pencil, Power, PowerOff, ImageOff } from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { REGIONS, VEHICLE_PHOTO_HINT, type Region } from "../../../../shared/const";
import { ActiveBadge, REGION_LOC, SHARED_REGION_LOC, THEAD_ROW, regionLoc } from "./base-data-shared";

type Row = {
  id: number;
  name: string;
  photoUrl: string | null;
  maxPassengers: number;
  maxLuggage: number;
  region: Region | null;
  sortOrder: number;
  isActive: "yes" | "no";
  notes: string | null;
};

export default function VehicleModels() {
  const { t } = useLanguage();
  const listQ = trpc.vehicleModel.list.useQuery();
  const rows = (listQ.data ?? []) as Row[];

  const setActive = trpc.vehicleModel.setActive.useMutation({ onSuccess: () => listQ.refetch() });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {t({ "zh-TW": "車款管理", "en-US": "Vehicle Models", "zh-CN": "车款管理" })}
          </h1>
          <p className="text-[13px] text-muted-foreground">
            {t({
              "zh-TW": "建單時的車款選單來源：名稱、照片、人數/行李上限。region 留空代表台灣/香港共用。",
              "en-US": "Vehicle options shown at booking: name, photo, pax/luggage limits. Empty region = shared.",
              "zh-CN": "建单时的车款选单来源：名称、照片、人数/行李上限。region 留空代表台湾/香港共用。",
            })}
          </p>
        </div>
        <ModelDialog onDone={() => listQ.refetch()} />
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-[13px] text-muted-foreground">
              {t({ "zh-TW": "尚無車款，點右上「新增車款」建立第一筆。", "en-US": "No vehicle models yet.", "zh-CN": "尚无车款，点右上「新增车款」建立第一笔。" })}
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className="p-3">{t({ "zh-TW": "照片", "en-US": "Photo", "zh-CN": "照片" })}</th>
                  <th className="p-3">{t({ "zh-TW": "名稱", "en-US": "Name", "zh-CN": "名称" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "人數上限", "en-US": "Max pax", "zh-CN": "人数上限" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "行李上限", "en-US": "Max luggage", "zh-CN": "行李上限" })}</th>
                  <th className="p-3">{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "排序", "en-US": "Sort", "zh-CN": "排序" })}</th>
                  <th className="p-3">{t({ "zh-TW": "狀態", "en-US": "Status", "zh-CN": "状态" })}</th>
                  <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="p-3">
                      {r.photoUrl ? (
                        <img src={r.photoUrl} alt={r.name} className="aspect-[5/2] w-20 rounded-md border border-border object-cover" />
                      ) : (
                        <div className="flex aspect-[5/2] w-20 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground/50">
                          <ImageOff className="h-4 w-4" />
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-medium">{r.name}</td>
                    <td className="p-3 text-right numeric">{r.maxPassengers}</td>
                    <td className="p-3 text-right numeric">{r.maxLuggage}</td>
                    <td className="p-3">{t(regionLoc(r.region))}</td>
                    <td className="p-3 text-right numeric">{r.sortOrder}</td>
                    <td className="p-3"><ActiveBadge active={r.isActive} /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <ModelDialog model={r} onDone={() => listQ.refetch()} />
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ id: r.id, isActive: r.isActive === "yes" ? "no" : "yes" })}
                      >
                        {r.isActive === "yes" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                        {r.isActive === "yes"
                          ? t({ "zh-TW": "停用", "en-US": "Disable", "zh-CN": "停用" })
                          : t({ "zh-TW": "啟用", "en-US": "Enable", "zh-CN": "启用" })}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ModelDialog({ model, onDone }: { model?: Row; onDone: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(model?.name ?? "");
  const [photoUrl, setPhotoUrl] = useState(model?.photoUrl ?? "");
  const [maxPassengers, setMaxPassengers] = useState(model?.maxPassengers ?? 4);
  const [maxLuggage, setMaxLuggage] = useState(model?.maxLuggage ?? 2);
  const [region, setRegion] = useState<"shared" | Region>(model?.region ?? "shared");
  const [sortOrder, setSortOrder] = useState(model?.sortOrder ?? 0);
  const [notes, setNotes] = useState(model?.notes ?? "");

  const m = trpc.vehicleModel.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  // 開啟時從 props/初始值重新同步草稿，避免殘留上一次新增/取消的輸入
  const changeOpen = (o: boolean) => {
    setOpen(o);
    if (o) {
      setName(model?.name ?? "");
      setPhotoUrl(model?.photoUrl ?? "");
      setMaxPassengers(model?.maxPassengers ?? 4);
      setMaxLuggage(model?.maxLuggage ?? 2);
      setRegion(model?.region ?? "shared");
      setSortOrder(model?.sortOrder ?? 0);
      setNotes(model?.notes ?? "");
    }
  };

  const save = () =>
    m.mutate({
      id: model?.id,
      name: name.trim(),
      photoUrl: photoUrl.trim() === "" ? null : photoUrl.trim(),
      maxPassengers,
      maxLuggage,
      region: region === "shared" ? null : region,
      sortOrder,
      notes: notes.trim() === "" ? null : notes.trim(),
    });

  const invalid =
    name.trim() === "" ||
    !Number.isFinite(maxPassengers) ||
    !Number.isFinite(maxLuggage) ||
    !Number.isFinite(sortOrder) ||
    maxPassengers < 1 ||
    maxLuggage < 0 ||
    sortOrder < 0;

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        {model ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "新增車款", "en-US": "New model", "zh-CN": "新增车款" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {model
              ? t({ "zh-TW": "編輯車款", "en-US": "Edit vehicle model", "zh-CN": "编辑车款" })
              : t({ "zh-TW": "新增車款", "en-US": "New vehicle model", "zh-CN": "新增车款" })}
          </DialogTitle>
          <DialogDescription>
            {t({
              "zh-TW": "照片先以 URL 提供（業主提供圖檔後貼上連結即可）。",
              "en-US": "Photo by URL for now — paste a link once assets are provided.",
              "zh-CN": "照片先以 URL 提供（业主提供图档后贴上链接即可）。",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "名稱", "en-US": "Name", "zh-CN": "名称" })}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "照片 URL", "en-US": "Photo URL", "zh-CN": "照片 URL" })}</Label>
            <Input value={photoUrl} placeholder="https://…" onChange={(e) => setPhotoUrl(e.target.value)} />
            <p className="text-[12px] text-muted-foreground">{t(VEHICLE_PHOTO_HINT)}</p>
            {photoUrl.trim() !== "" && (
              // 預覽鎖 2.5:1，貼上 URL 當下就看得到建單頁實際的裁切結果
              <img
                src={photoUrl.trim()}
                alt={t({ "zh-TW": "預覽", "en-US": "Preview", "zh-CN": "预览" })}
                className="aspect-[5/2] w-48 rounded-md border border-border object-cover"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "人數上限", "en-US": "Max passengers", "zh-CN": "人数上限" })}</Label>
              <Input type="number" min={1} max={99} value={maxPassengers} onChange={(e) => setMaxPassengers(Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "行李上限", "en-US": "Max luggage", "zh-CN": "行李上限" })}</Label>
              <Input type="number" min={0} max={99} value={maxLuggage} onChange={(e) => setMaxLuggage(Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "區域", "en-US": "Region", "zh-CN": "区域" })}</Label>
              <Select value={region} onChange={(e) => setRegion(e.target.value as "shared" | Region)}>
                <option value="shared">{t(SHARED_REGION_LOC)}</option>
                {REGIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(REGION_LOC[r])}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t({ "zh-TW": "排序", "en-US": "Sort order", "zh-CN": "排序" })}</Label>
              <Input type="number" min={0} max={9999} value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t({ "zh-TW": "備註", "en-US": "Notes", "zh-CN": "备注" })}</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={save} disabled={m.isPending || invalid}>
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
